import React, { useEffect, useState, useContext } from 'react';
import { Formik } from 'formik';
import { useHistory, useParams } from 'react-router-dom';
import { StatusCodes } from 'http-status-codes';
import { Form } from 'react-bootstrap';

import WhitePad from '../../../components/common/WhitePad/WhitePad';
import FormRow from '../../../components/common/forms/Row/FormRow';
import TextInputComponent from '../../../components/common/forms/TextInput/TextInputComponent';
import validationSchema from '../validationSchema';
import validationSchemaCreate from '../validationSchemaCreate';
import AuthApi from '../../../services/api/AuthApi';
import PrimaryButton from '../../../components/common/forms/Button/PrimaryButton';
import TitleComponent from '../../../components/common/forms/Title/TitleComponent';
import AutocompleteStaticComponent from '~/src/components/common/forms/Autcomplete/AutocompleteStaticComponent';
import { API_URL, ROLES } from '~/src/utils/constants';
import { autocompleteToList, constantsToAutocomplete, grantedForRole } from '~/src/utils/misc';
import RolesDependent from '~/src/components/common/RolesDependent';
import MyContext from '~/src/services/context';
import FormWrapper from '~/src/components/common/forms/FormWrapper/FormWrapper';
import CheckboxInputComponent from '~/src/components/common/forms/CheckboxInput/CheckboxInputComponent';
import AutocompleteComponent from '~/src/components/common/forms/Autcomplete/AutocompleteComponent';

export default function UserEdit(): JSX.Element {
  const { id } = useParams();
  const context = useContext(MyContext);
  const [itemData, setItemData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
    isDisabled: false,
    dataArrived: false,
    roles: [],
    availableCustomersForWarehouseOwner: [],
    availableSuppliersForWarehouseOwner: [],
    defaultWarehouseOwnerId: '',
    svilaClientId: null,
  });
  const history = useHistory();

  const saveItem = async (values, setSubmitting) => {
    setSubmitting(true);
    let result = null;

    if (id) {
      const availableCustomersIds = values.availableCustomersForWarehouseOwner.map((obj) => obj.id).join(',');
      const availableSuppliersIds = values.availableSuppliersForWarehouseOwner.map((obj) => obj.id).join(',');

      const fixedAutocomplete = {
        ...values,
        roles: autocompleteToList(values.roles),
        availableCustomersForWarehouseOwner: availableCustomersIds,
        availableSuppliersForWarehouseOwner: availableSuppliersIds
      };
      const hasPermissions = grantedForRole(ROLES.SUPER_ADMIN, context.user?.role);

      if (hasPermissions) {
        result = await AuthApi.update(id, fixedAutocomplete);
      } else {
        result = await AuthApi.updateWithoutRoles(id, fixedAutocomplete);
      }
    } else {
      const availableCustomersIds = values.availableCustomersForWarehouseOwner.map((obj) => obj.id).join(',');
      const availableSuppliersIds = values.availableSuppliersForWarehouseOwner.map((obj) => obj.id).join(',');
      const fixedAutocomplete = {
        ...values,
        roles: autocompleteToList(values.roles),
        availableCustomersForWarehouseOwner: availableCustomersIds,
        availableSuppliersForWarehouseOwner: availableSuppliersIds
      };

      result = await AuthApi.create(fixedAutocomplete);
    }

    if (result.status === StatusCodes.OK) {
      history.push('/users');
    } else {
      setSubmitting(false);
    }
  };

  const getItem = async () => {
    const result = await AuthApi.get(id);
    if (result.status === StatusCodes.OK) {
      const newItemData = { ...itemData, ...result.data, dataArrived: true };
      const roles = [];
      result.data.roles.forEach((role) => {
        const roleObject = {
          label: role,
          value: role,
        };
        roles.push(roleObject);
      });
      newItemData.roles = roles;

      if (result.data.availableCustomers == null) {
        newItemData.availableCustomersForWarehouseOwner = [];
      } else {
        newItemData.availableCustomersForWarehouseOwner = result.data.availableCustomers;
      }

      if (result.data.availableSuppliers == null) {
        newItemData.availableSuppliersForWarehouseOwner = [];
      } else {
        newItemData.availableSuppliersForWarehouseOwner = result.data.availableSuppliers;
      }
      newItemData.defaultWarehouseOwnerId = newItemData.defaultWarehouseOwner?.id;
      setItemData(newItemData);
    }
  };

  useEffect(() => {
    if (id) {
      getItem(id);
    }
  }, []);

  const filterRoles = (roles) => {
    if (context.user?.role === ROLES.SVILA_CLIENT_ADMIN) {
      return roles.filter(
        (role) => role.value !== ROLES.SUPER_ADMIN && role.value !== ROLES.ADMIN
      );
    }
    return roles;
  };

  return (
    <>
      <WhitePad>
        <TitleComponent title={`${id ? 'Edit ' : 'Create '}User`} />

        <FormWrapper>
          {id && itemData.dataArrived === false ? null : (
            <Formik
              initialValues={{
                email: itemData.email,
                firstName: itemData.firstName,
                lastName: itemData.lastName,
                password: itemData.password,
                confirmPassword: itemData.confirmPassword,
                isDisabled: itemData.isDisabled,
                roles: itemData.roles,
                availableCustomersForWarehouseOwner: itemData.availableCustomersForWarehouseOwner,
                availableSuppliersForWarehouseOwner: itemData.availableSuppliersForWarehouseOwner,
                defaultWarehouseOwnerId: itemData.defaultWarehouseOwnerId,
                svilaClientId: itemData.svilaClientId || null,
              }}
              validationSchema={id ? validationSchema : validationSchemaCreate}
              onSubmit={(values, { setSubmitting }) => {
                return saveItem(values, setSubmitting);
              }}
            >
              {({
                values,
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                errors,
                setFieldValue,
                touched,
              }) => (
                <form onSubmit={handleSubmit} autoComplete="off">
                  <div>
                    <FormRow>
                      <TextInputComponent
                        name="firstName"
                        label="First Name"
                        value={values.firstName}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        cols={12}
                        errors={errors}
                      />
                    </FormRow>
                    <FormRow>
                      <TextInputComponent
                        name="lastName"
                        label="Last Name"
                        value={values.lastName}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        cols={12}
                        errors={errors}
                      />
                    </FormRow>
                    <FormRow>
                      <TextInputComponent
                        name="email"
                        label="Email"
                        value={values.email}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        cols={12}
                        errors={errors}
                      />
                    </FormRow>
                    <FormRow>
                      <TextInputComponent
                        name="password"
                        label="Password"
                        value={values.password}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        type="password"
                        cols={12}
                        autocomplete="new-password"
                        errors={errors}
                      />
                    </FormRow>
                    <FormRow>
                      <TextInputComponent
                        name="confirmPassword"
                        label="Confirm Password"
                        value={values.confirmPassword}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        type="password"
                        cols={12}
                        autocomplete="new-password"
                        errors={errors}
                      />
                    </FormRow>
                    {id && (
                      <FormRow>
                        <div className=" col-sm-12" style={{ margin: '5px 0' }}>
                          <CheckboxInputComponent
                            name="isDisabled"
                            label="is Disabled"
                            cols={12}
                            onChange={handleChange}
                            isInvalid={!!errors.isDisabled}
                            feedback={errors.isDisabled}
                            checked={values.isDisabled}
                            value={values.isDisabled}
                          />
                        </div>
                      </FormRow>
                    )}

                    <FormRow>
                      <RolesDependent roles={[ROLES.SUPER_ADMIN, ROLES.SVILA_CLIENT_ADMIN]}>
                        <AutocompleteStaticComponent
                          options={filterRoles(constantsToAutocomplete(ROLES))}
                          name="roles"
                          initialValue={values.roles}
                          onChange={setFieldValue}
                          getOptionLabel={(option) => `${option.label}`}
                          cols={12}
                          errors={errors}
                          touched={touched}
                          isMulti
                        />
                      </RolesDependent>
                    </FormRow>
                    <FormRow>
                      <RolesDependent roles={[ROLES.SUPER_ADMIN, ROLES.ADMIN]}>
                        <AutocompleteComponent
                          url={`${API_URL}/svila-clients/get-all`}
                          name="svilaClientId"
                          initialValue={values.svilaClientId}
                          onChange={(name, value) => {
                            setFieldValue(name, value);
                          }}
                          getOptionLabel={(option) => `${option.name}`}
                          errorMessage={errors.svilaClientId}
                          cols={12}
                          label="Svila Client"
                        />
                      </RolesDependent>
                    </FormRow>
                  </div>
                  {/* 
                Add new formrow that contains all the warehouse owners
                */}
                  <FormRow>
                    <AutocompleteComponent
                      url={`${API_URL}/contractors/customers/get`}
                      name="availableCustomersForWarehouseOwner"
                      label="Available Customers for Warehouse Owner"
                      initialValue={values.availableCustomersForWarehouseOwner}
                      onChange={setFieldValue}
                      getOptionLabel={(option) => `${option.name}`}
                      isMulti={true}
                    />
                    <AutocompleteComponent
                      url={`${API_URL}/contractors/suppliers/get`}
                      name="availableSuppliersForWarehouseOwner"
                      label="Available Suppliers for Warehouse Owner"
                      initialValue={values.availableSuppliersForWarehouseOwner}
                      onChange={setFieldValue}
                      getOptionLabel={(option) => `${option.name}`}
                      isMulti={true}
                    />
                    <AutocompleteComponent
                      url={`${API_URL}/contractors/warehouse-owners/get`}
                      name="defaultWarehouseOwnerId"
                      label="Default Warehouse Owner"
                      initialValue={values.defaultWarehouseOwnerId}
                      onChange={setFieldValue}
                      getOptionLabel={(option) => {
                        return `${option.name}`;
                      }}
                      isMulti={false}
                    />
                  </FormRow>

                  <br />
                  <PrimaryButton variant="primary" isSubmitting={isSubmitting} type="submit" title="Save" />
                </form>
              )}
            </Formik>
          )}
        </FormWrapper>
      </WhitePad>
    </>
  );
}
